import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';

import Tooltip from 'components/Tooltip';
import RMSXGrid from 'components/RMSXGrid';
import PrintsSearch2 from 'components/RMSSearch/components/PrintsSearch2';
import QuickFilterSearch from 'components/RMSSearch/components/QuickFilterSearch';

import { notify } from 'reducers/NotifierReducer';
import { handleError } from 'reducers/ErrorReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { showVehicleEditReg } from 'reducers/PersonDialogsReducer';
import { getPeopleVehicle, deleteVehicleReg } from 'reducers/SearchReducer';
import { displayDate, displayDateTime } from 'reducers/TimeReducer';

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(3),
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  lookup: {
    maxWidth: 550,
    marginBottom: 20,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  lookupActions: {
    marginLeft: 8,
    '& button': {
      marginLeft: 8,
    },
    '& svg': {
      marginRight: 8,
    },
  },
}));

const defaultColumns = [
  // { field: 'VehicleID', headerName: 'Vehicle ID', width: 150, hide: false },
  { field: 'ptsRegistrationID', headerName: 'RegistrationID', width: 150, hide: false },
  { field: 'PlateType', headerName: 'Plate Type', width: 150, hide: false },

  { field: 'PlateColor', headerName: 'Plate Color', width: 150, hide: false },

  { field: 'PlateNumber', headerName: 'Plate Number', width: 150, hide: false },
  { field: 'PlateState', headerName: 'Plate State', width: 150, hide: false },
  // { field: 'Make', headerName: 'Make', width: 150, hide: false },
  // { field: 'Model', headerName: 'Model', width: 150, hide: false },
  // { field: 'Year', headerName: 'Year', width: 150, hide: false },
  { field: 'PlateBackgroundText', headerName: 'BackgroundText', width: 150, hide: false },
  { field: 'PlateDetail', headerName: 'PlateDetail', width: 150, hide: false },
  { field: 'PlateStatus', headerName: 'PlateStatus', width: 150, hide: false },
  {
    field: 'PlateExpiration',
    headerName: 'Plate Expiration',
    valueFormatter: (params) => displayDate(params.value),
  },
  { field: 'OdometerReading', headerName: 'OdometerReading', width: 150, hide: false },
  { field: 'InsuranceCompany', headerName: 'Insurance Company', width: 150, hide: false },
  { field: 'VehicleValue', headerName: 'Vehicle Value', width: 150, hide: false },
  {
    field: 'Created',
    headerName: 'Created',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 150, hide: false },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 150, hide: false },
];

const PersonVehicle = (props) => {
  const classes = useStyles();
  const { personId, dataUpdate } = props;

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selection, setSelection] = useState(null);
  const [columns, setColumns] = useState(defaultColumns);
  const perms = getFullPermissions('globals', 'People', 'any');
  const canEditPerson = perms.Edit;
  const mountedRef = useRef(true);

  useEffect(() => {
    personId && getData();
  }, []);

  useEffect(() => {
    if (dataUpdate && dataUpdate.type === 'vehicle-reg') getData();
  }, [dataUpdate]);

  const getData = async () => {
    try {
      const data = await getPeopleVehicle(personId);
      if (!mountedRef.current) return;
      setRows(processData(data.data));
      setLoaded(true);
    } catch (err) {
      props.handleError(err);
    }
  };

  const processData = (data) => {
    return data.map((row) => ({ ...row, id: row.ptsRegistrationID }));
  };

  const edit = () => {
    const item = getCurrentItem();
    item && props.showVehicleEditReg({ ...item, disableParty: true, type: 'person' });
  };

  const del = () => {
    const item = getCurrentItem();
    props.showSpinner();

    deleteVehicleReg(item.ptsRegistrationID)
      .then(() => {
        props.notify('Vehicle Registration Deleted', 'warning');
        props.notifyDataUpdate({ type: 'vehicle-reg' });
      })
      .catch(props.handleError)
      .finally(props.hideSpinner);
  };

  const getCurrentItem = () => {
    const data = rows.find((r) => r.ptsRegistrationID === selection);
    return data ? data : null;
  };

  const renderActions = () => {
    const onAdd = () => {
      props.showVehicleEditReg({
        PartyType: 'Person',
        ptsParentID: personId,
        disableParty: true,
        type: 'person',
      });
    };
    return (
      <div className={classes.actions}>
        <Tooltip title="Print">
          <PrintsSearch2 title="Vehicle Registrations" data={filteredRows} cols={columns} />
        </Tooltip>
        <Tooltip title="Add Registration">
          <Fab size="small" color="secondary" disabled={!canEditPerson} onClick={onAdd}>
            <AddIcon />
          </Fab>
        </Tooltip>
        {selection !== null && (
          <>
            <Tooltip title="Edit Registration">
              <Fab size="small" color="secondary" disabled={!canEditPerson} onClick={edit}>
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Delete Registration">
              <Fab size="small" color="secondary" disabled={!canEditPerson} onClick={del}>
                <DeleteIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.filterWrap}>
        <QuickFilterSearch
          rows={rows}
          columns={columns}
          setFilteredRows={setFilteredRows}
          setData={props.setData}
          style={{ marginTop: 0, paddingBottom: 14 }}
        />
        {renderActions()}
      </div>
      <RMSXGrid
        name="PeopleVehicles"
        columns={columns}
        setColumns={setColumns}
        rows={filteredRows}
        loading={!loaded}
        setSelection={setSelection}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  editPeopleEmployment: state.dialogs.editPeopleEmployment,
  dataUpdate: state.dataUpdate,
});

export default connect(mapStateToProps, {
  handleError,
  showSpinner,
  hideSpinner,
  showVehicleEditReg,
  notify,
  notifyDataUpdate,
})(PersonVehicle);
